export interface AdminAccountUser {
  id: string;
  username: string;
  email: string;
  displayName: string;
  registrationApplications: string[];
  enabled: boolean;
  roles: string[];
  createdDate: string | null;
  lastLoginDate: string | null;
}

export interface AdminAccountPaymentDebugResponse {
  userId: string;
  currentPlanCode: string;
  activeSubscription: AdminAccountActiveSubscription | null;
  recentOrders: AdminAccountRecentOrder[];
  recentSubscriptionEvents: AdminAccountSubscriptionEvent[];
}

export interface AdminAccountPaymentSummaryResponse {
  userId: string;
  appGroups: AdminAccountPaymentSummaryAppGroup[];
}

export interface AdminAccountReferralDebugResponse {
  userId: string;
  referralCode: AdminAccountReferralCode | null;
  pointBalance: number;
  currentConfig: AdminAccountReferralConfig | null;
  stats: AdminAccountReferralStats | null;
  usageAsReferee: AdminAccountReferralUsage[];
  usageAsReferrer: AdminAccountReferralUsage[];
  recentPointTransactions: AdminAccountReferralPointTransaction[];
}

export interface AdminAccountPaymentSummaryAppGroup {
  appKey: string;
  appLabel: string;
  activeSubscriptionCount: number;
  currentAccessSource?: AdminAccountCurrentAccessSource;
  currentAccessReference?: string | null;
  currentAccessStartedAt?: string | null;
  offerTrialState?: AdminAccountOfferTrialState;
  offerId?: string | null;
  offerTrialEndsAt?: string | null;
  offerPlatform?: string | null;
  couponUsageCount?: number;
  latestCouponCode?: string | null;
  latestCouponType?: string | null;
  latestCouponTrialDays?: number | null;
  latestCouponUsedAt?: string | null;
  nextPaymentState?: AdminAccountNextPaymentState;
  nextPaymentAt?: string | null;
  nextPaymentPlatform?: string | null;
  paidSubscriberActivityDates?: string[];
  storeSubscriptionActivityDates?: string[];
  offerTrialActivityDates?: string[];
  firstLoginBenefitActivityDates?: string[];
  couponUsageDates?: string[];
  referralRewardActivityDates?: string[];
  manualBenefitActivityDates?: string[];
  summaries: AdminAccountPaymentSummaryItem[];
}

export type AdminAccountCurrentAccessSource =
  | 'NONE'
  | 'PAID_SUBSCRIPTION'
  | 'STORE_SUBSCRIPTION'
  | 'STORE_OFFER_TRIAL'
  | 'COUPON'
  | 'REFERRAL_COUPON'
  | 'REFERRAL_REWARD'
  | 'FIRST_LOGIN_BENEFIT'
  | 'MANUAL_BENEFIT'
  | 'OTHER_BENEFIT';

export type AdminAccountNextPaymentState =
  | 'SCHEDULED'
  | 'STORE_AUTORENEWING'
  | 'STORE_NOT_RENEWING'
  | 'STORE_STATUS_UNKNOWN'
  | 'EXTERNAL_PROVIDER'
  | 'PAYMENT_FAILURE'
  | 'NONE';
export type AdminAccountOfferTrialState = 'ACTIVE' | 'ENDED' | 'OFFER_APPLIED' | 'NONE';

export interface AdminAccountReferralCode {
  code: string;
  userId: string;
  createdAt: string | null;
}

export interface AdminAccountReferralConfig {
  tier: string;
  maxInvites: number;
  referrerDays: number;
  refereeDays: number;
  referrerPoints: number;
  refereePoints: number;
}

export interface AdminAccountReferralStats {
  inviteCount: number;
  remainingInvites: number;
  maxInvites: number;
  tier: string;
}

export interface AdminAccountReferralUsage {
  id: string;
  code: string;
  referrerId: string;
  refereeId: string;
  usedAt: string | null;
  status: string;
  proGrantedAt: string | null;
}

export interface AdminAccountReferralPointTransaction {
  id: string;
  userId: string;
  amount: number;
  transactionType: string;
  description: string | null;
  createdAt: string | null;
  expiresAt: string | null;
}

export interface AdminAccountPaymentSummaryItem {
  productPlanCode: string;
  planLabel: string;
  status: string;
  billingCycle: string | null;
  paymentPlatform: string | null;
  expiresAt: string | null;
  primary: boolean;
  failureCategory: string | null;
}

export interface AdminAccountActiveSubscription {
  id: number | string;
  orderId: string | null;
  productName: string;
  productPlanCode: string;
  status: string;
  startDate: string;
  nextBillingDate: string | null;
  endDate: string;
  paymentPlatform: string | null;
  lastPaymentDate: string | null;
  lastPaymentResult: string | null;
  orderFailReason: string | null;
  failureCategory: string | null;
  /** 연결 주문에 진행 중인 환불(PENDING·APPROVED·PROVIDER_PENDING)이 있으면 true. */
  refundPending?: boolean | null;
}

export interface AdminAccountSubscriptionEvent {
  id: number | string;
  eventType: string;
  description: string | null;
  createdAt: string | null;
  previousEndDate: string | null;
  newEndDate: string | null;
  deltaDays: number | null;
}

export interface AdminAccountRecentOrder {
  id: number | string;
  orderId: string | null;
  productName: string;
  productPlanCode: string;
  amount: number;
  currency: string | null;
  billingCycle: string | null;
  paymentPlatform: string | null;
  status: string;
  createdAt: string;
  refundRequestId: number | string | null;
  refundRequestStatus: string | null;
  refundRequestedAt?: string | null;
  failReason: string | null;
  failureCategory: string | null;
}

export type AdminAccountSortMode = 'created_desc' | 'created_asc' | 'last_login_desc' | 'email_asc';

export type AdminAccountSubscriptionFilter = 'all' | 'active' | 'offer_trial' | 'coupon_used' | 'next_payment' | 'payment_failure';

export interface AdminAccountSortOption {
  value: AdminAccountSortMode;
  label: string;
}

export interface AdminAccountSubscriptionFilterOption {
  value: AdminAccountSubscriptionFilter;
  label: string;
}

export interface AdminAccountSignupTrend {
  currentCount: number;
  previousCount: number;
  deltaCount: number;
  deltaPercent: number | null;
}

export interface AdminAccountSignupTrendSummary {
  week: AdminAccountSignupTrend;
  month: AdminAccountSignupTrend;
}

export interface AdminAccountPaymentInsightTrendSummary {
  confirmedPaidSubscribers: AdminAccountSignupTrend;
  googlePlayPurchases: AdminAccountSignupTrend;
  offerTrials: AdminAccountSignupTrend;
  firstLoginBenefits: AdminAccountSignupTrend;
  couponUsers: AdminAccountSignupTrend;
  referralRewards: AdminAccountSignupTrend;
  manualBenefits: AdminAccountSignupTrend;
}

export { ADMIN_ACCOUNT_PAGE_SIZE, paginateAdminAccountItems, type AdminAccountPaginationSlice } from './accountPagination';
export const ADMIN_ACCOUNT_DEFAULT_SORT_MODE: AdminAccountSortMode = 'created_desc';
export const ADMIN_ACCOUNT_SORT_OPTIONS: AdminAccountSortOption[] = [
  { value: 'created_desc', label: '최근 가입순' },
  { value: 'created_asc', label: '오래된 가입순' },
  { value: 'last_login_desc', label: '최근 로그인순' },
  { value: 'email_asc', label: '이메일 오름차순' },
];
export const ADMIN_ACCOUNT_SUBSCRIPTION_FILTER_OPTIONS: AdminAccountSubscriptionFilterOption[] = [
  { value: 'all', label: '전체 계정' },
  { value: 'active', label: '현재 이용 중' },
  { value: 'offer_trial', label: 'Offer 무료체험 이력' },
  { value: 'coupon_used', label: '쿠폰 사용 이력' },
  { value: 'next_payment', label: '자동결제 예정' },
  { value: 'payment_failure', label: '구독 결제 실패' },
];
const ADMIN_ACCOUNT_PENDING_ORDER_STATUSES = new Set(['PENDING', 'PROCESSING']);

const asString = (value: unknown): string | null => {
  if (typeof value === 'string' && value.trim()) return value;
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return null;
};

const unwrapJacksonEnvelope = (value: unknown): unknown => {
  if (!Array.isArray(value)) {
    return value;
  }

  if (
    value.length === 2 &&
    typeof value[0] === 'string' &&
    /^[A-Za-z_$][\w$]*(?:\.[A-Za-z_$][\w$]*)+$/.test(value[0])
  ) {
    return unwrapJacksonEnvelope(value[1]);
  }

  return value.map((item) => unwrapJacksonEnvelope(item));
};

const normalizeStringCollection = (value: unknown): string[] => {
  const unwrapped = unwrapJacksonEnvelope(value);
  if (!Array.isArray(unwrapped)) {
    return [];
  }

  return unwrapped.filter((item): item is string => typeof item === 'string' && item.trim().length > 0);
};

const normalizeRegistrationApplications = (record: Record<string, unknown>): string[] => {
  const pluralValue = record['registrationApplications'];
  const pluralApplications = normalizeStringCollection(pluralValue);
  if (pluralApplications.length > 0 || pluralValue !== undefined) {
    return [...new Set(pluralApplications.map((application) => application.trim().toUpperCase()))];
  }

  const legacyApplication = asString(record['registrationApplication']);
  return legacyApplication ? [legacyApplication.trim().toUpperCase()] : [];
};

const normalizeNumber = (value: unknown): number => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return 0;
};

const normalizeOptionalNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') return null;
  const normalized = normalizeNumber(value);
  return normalized;
};

const ZERO_DECIMAL_CURRENCIES = new Set(['KRW', 'JPY']);
const ADMIN_ACCOUNT_REFUNDABLE_ORDER_STATUSES = new Set(['PAID', 'COMPLETED']);
const ADMIN_ACCOUNT_CANCELABLE_SUBSCRIPTION_STATUSES = new Set(['ACTIVE', 'PAYMENT_FAILURE']);
const ADMIN_ACCOUNT_CURRENT_ACCESS_STATUSES = new Set(['ACTIVE', 'ON_HOLD', 'IN_GRACE_PERIOD']);
const ADMIN_ACCOUNT_PROCESSABLE_REFUND_REQUEST_STATUSES = new Set(['PENDING', 'APPROVED', 'FAILED']);
const ADMIN_ACCOUNT_SUBSCRIPTION_PAYMENT_FAILURE_CATEGORIES = new Set([
  'renewal_payment_failure',
  'billing_payment_failure',
]);

const normalizeCurrencyCode = (value: unknown): string | null => {
  const raw = asString(value);
  return raw ? raw.trim().toUpperCase() : null;
};

const normalizePaymentPlatform = (value: unknown): string | null => {
  const raw = asString(value);
  if (!raw) return null;

  const normalized = raw.trim().toUpperCase().replace(/[\s-]+/g, '_');
  if (normalized.includes('GOOGLE') || normalized === 'PLAY' || normalized === 'ANDROID') {
    return 'GOOGLE_PLAY';
  }
  if (normalized.includes('NICE')) {
    return 'NICEPAY';
  }
  if (normalized.includes('PAYPLE')) {
    return 'PAYPLE';
  }
  if (normalized.includes('TOSS')) {
    return 'TOSS_PAYMENTS';
  }
  if (normalized === 'CARD' || raw.includes('카드')) {
    return 'NICEPAY';
  }

  return normalized;
};

const parseDateLike = (value: string | null | undefined): Date | null => {
  if (!value) return null;

  const trimmed = value.trim();
  if (!trimmed) return null;

  if (/^\d+(\.\d+)?$/.test(trimmed)) {
    const numeric = Number(trimmed);
    if (Number.isFinite(numeric)) {
      const milliseconds = numeric < 1_000_000_000_000 ? numeric * 1000 : numeric;
      const parsedNumeric = new Date(milliseconds);
      if (!Number.isNaN(parsedNumeric.getTime())) {
        return parsedNumeric;
      }
    }
  }

  const parsed = new Date(trimmed);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const sortAppGroups = (groups: AdminAccountPaymentSummaryAppGroup[]): AdminAccountPaymentSummaryAppGroup[] => {
  return [...groups].sort((left, right) => {
    const leftPriority = left.appKey.toUpperCase() === 'SAJU' ? 0 : 1;
    const rightPriority = right.appKey.toUpperCase() === 'SAJU' ? 0 : 1;
    if (leftPriority !== rightPriority) {
      return leftPriority - rightPriority;
    }
    return left.appLabel.localeCompare(right.appLabel, 'ko-KR');
  });
};

export const getAdminAccountUserKey = (account: AdminAccountUser): string => {
  return account.id;
};

export const getAdminAccountUserLabel = (account: AdminAccountUser): string => {
  return account.displayName.trim() || account.username.trim() || account.email.trim() || account.id || '알 수 없음';
};

export const getAdminAccountUserSearchText = (account: AdminAccountUser): string => {
  return [
    account.email,
    account.username,
    account.displayName,
    account.id,
  ].join(' ').toLowerCase();
};

export const hasAdminAccountActiveSubscription = (
  summary: AdminAccountPaymentSummaryResponse | null | undefined,
): boolean => summary?.appGroups.some((group) => group.activeSubscriptionCount > 0) ?? false;

export const hasAdminAccountCouponUsage = (
  summary: AdminAccountPaymentSummaryResponse | null | undefined,
): boolean => summary?.appGroups.some((group) => (group.couponUsageCount ?? 0) > 0) ?? false;

export const hasAdminAccountActiveOfferTrial = (
  summary: AdminAccountPaymentSummaryResponse | null | undefined,
): boolean => summary?.appGroups.some((group) => group.offerTrialState === 'ACTIVE') ?? false;

export const hasAdminAccountOfferTrialHistory = (
  summary: AdminAccountPaymentSummaryResponse | null | undefined,
): boolean => summary?.appGroups.some((group) =>
  (group.offerTrialState != null && group.offerTrialState !== 'NONE')
  || Boolean(group.offerId)
  || Boolean(group.offerTrialEndsAt)
  || (group.offerTrialActivityDates?.length ?? 0) > 0
) ?? false;

export const hasAdminAccountScheduledNextPayment = (
  summary: AdminAccountPaymentSummaryResponse | null | undefined,
): boolean => summary?.appGroups.some((group) =>
  group.nextPaymentState === 'SCHEDULED' || group.nextPaymentState === 'STORE_AUTORENEWING'
) ?? false;

export const hasAdminAccountSubscriptionPaymentFailure = (
  summary: AdminAccountPaymentSummaryResponse | null | undefined,
): boolean => summary?.appGroups.some((group) =>
  group.summaries.some((item) => {
    const status = item.status.trim().toUpperCase();
    const failureCategory = item.failureCategory?.trim().toLowerCase() || null;
    return status === 'PAYMENT_FAILURE'
      || Boolean(failureCategory && ADMIN_ACCOUNT_SUBSCRIPTION_PAYMENT_FAILURE_CATEGORIES.has(failureCategory));
  })
) ?? false;

export const filterAdminAccountsBySubscription = (
  accounts: AdminAccountUser[],
  summariesByUserId: Record<string, AdminAccountPaymentSummaryResponse>,
  filter: AdminAccountSubscriptionFilter,
): AdminAccountUser[] => {
  if (filter === 'all') {
    return accounts;
  }

  return accounts.filter((account) => {
    const summary = summariesByUserId[normalizeAdminAccountUserId(account)];
    switch (filter) {
      case 'active':
        return hasAdminAccountActiveSubscription(summary);
      case 'offer_trial':
        return hasAdminAccountOfferTrialHistory(summary);
      case 'coupon_used':
        return hasAdminAccountCouponUsage(summary);
      case 'next_payment':
        return hasAdminAccountScheduledNextPayment(summary);
      case 'payment_failure':
        return hasAdminAccountSubscriptionPaymentFailure(summary);
      default:
        return true;
    }
  });
};

const getAdminAccountDateValue = (value: string | null | undefined): number | null => {
  const parsed = parseDateLike(value);
  if (!parsed) {
    return null;
  }
  return parsed.getTime();
};

const compareAdminAccountDateValues = (
  left: string | null | undefined,
  right: string | null | undefined,
  direction: 'asc' | 'desc',
): number => {
  const leftValue = getAdminAccountDateValue(left);
  const rightValue = getAdminAccountDateValue(right);
  if (leftValue === null && rightValue === null) {
    return 0;
  }
  if (leftValue === null) {
    return 1;
  }
  if (rightValue === null) {
    return -1;
  }
  return direction === 'asc' ? leftValue - rightValue : rightValue - leftValue;
};

const compareAdminAccountLabels = (left: AdminAccountUser, right: AdminAccountUser): number => {
  const leftLabel = getAdminAccountUserLabel(left).toLowerCase();
  const rightLabel = getAdminAccountUserLabel(right).toLowerCase();
  return leftLabel.localeCompare(rightLabel, 'ko-KR');
};

export const sortAdminAccountUsers = (
  accounts: AdminAccountUser[],
  sortMode: AdminAccountSortMode = ADMIN_ACCOUNT_DEFAULT_SORT_MODE,
): AdminAccountUser[] => {
  return [...accounts].sort((left, right) => {
    let result = 0;
    switch (sortMode) {
      case 'created_asc':
        result = compareAdminAccountDateValues(left.createdDate, right.createdDate, 'asc');
        break;
      case 'last_login_desc':
        result = compareAdminAccountDateValues(left.lastLoginDate, right.lastLoginDate, 'desc');
        break;
      case 'email_asc':
        result = left.email.toLowerCase().localeCompare(right.email.toLowerCase(), 'ko-KR');
        break;
      case 'created_desc':
      default:
        result = compareAdminAccountDateValues(left.createdDate, right.createdDate, 'desc');
        break;
    }

    return result || compareAdminAccountLabels(left, right);
  });
};

const startOfAdminAccountLocalDay = (value: Date): Date => {
  const date = new Date(value);
  date.setHours(0, 0, 0, 0);
  return date;
};

const startOfAdminAccountLocalWeek = (value: Date): Date => {
  const date = startOfAdminAccountLocalDay(value);
  const mondayOffset = (date.getDay() + 6) % 7;
  date.setDate(date.getDate() - mondayOffset);
  return date;
};

const startOfAdminAccountLocalMonth = (value: Date): Date => {
  const date = startOfAdminAccountLocalDay(value);
  date.setDate(1);
  return date;
};

const addAdminAccountDays = (value: Date, days: number): Date => {
  const date = new Date(value);
  date.setDate(date.getDate() + days);
  return date;
};

const addAdminAccountMonths = (value: Date, months: number): Date => {
  const date = new Date(value);
  date.setMonth(date.getMonth() + months);
  return date;
};

const countAdminAccountCreatedBetween = (
  accounts: AdminAccountUser[],
  start: Date,
  end: Date,
): number => {
  const startTime = start.getTime();
  const endTime = end.getTime();
  return accounts.filter((account) => {
    const createdTime = getAdminAccountDateValue(account.createdDate);
    return createdTime !== null && createdTime >= startTime && createdTime < endTime;
  }).length;
};

const buildAdminAccountSignupTrend = (
  accounts: AdminAccountUser[],
  currentStart: Date,
  currentEnd: Date,
  previousStart: Date,
): AdminAccountSignupTrend => {
  const currentCount = countAdminAccountCreatedBetween(accounts, currentStart, currentEnd);
  const previousCount = countAdminAccountCreatedBetween(accounts, previousStart, currentStart);
  const deltaCount = currentCount - previousCount;
  const deltaPercent = previousCount === 0
    ? (currentCount === 0 ? 0 : null)
    : Math.round((deltaCount / previousCount) * 1000) / 10;

  return {
    currentCount,
    previousCount,
    deltaCount,
    deltaPercent,
  };
};

export const summarizeAdminAccountSignupTrends = (
  accounts: AdminAccountUser[],
  now: Date = new Date(),
): AdminAccountSignupTrendSummary => {
  const weekStart = startOfAdminAccountLocalWeek(now);
  const nextWeekStart = addAdminAccountDays(weekStart, 7);
  const previousWeekStart = addAdminAccountDays(weekStart, -7);
  const monthStart = startOfAdminAccountLocalMonth(now);
  const nextMonthStart = addAdminAccountMonths(monthStart, 1);
  const previousMonthStart = addAdminAccountMonths(monthStart, -1);

  return {
    week: buildAdminAccountSignupTrend(accounts, weekStart, nextWeekStart, previousWeekStart),
    month: buildAdminAccountSignupTrend(accounts, monthStart, nextMonthStart, previousMonthStart),
  };
};

type AdminAccountPaymentActivityDateField =
  | 'paidSubscriberActivityDates'
  | 'storeSubscriptionActivityDates'
  | 'offerTrialActivityDates'
  | 'firstLoginBenefitActivityDates'
  | 'couponUsageDates'
  | 'referralRewardActivityDates'
  | 'manualBenefitActivityDates';

const countAdminAccountPaymentActivityBetween = (
  summariesByUserId: Record<string, AdminAccountPaymentSummaryResponse>,
  field: AdminAccountPaymentActivityDateField,
  start: Date,
  end: Date,
): number => {
  const startTime = start.getTime();
  const endTime = end.getTime();
  return Object.values(summariesByUserId).filter((summary) =>
    summary.appGroups.some((group) => (group[field] ?? []).some((value) => {
      const activityTime = getAdminAccountDateValue(value);
      return activityTime !== null && activityTime >= startTime && activityTime < endTime;
    })),
  ).length;
};

const buildAdminAccountPaymentActivityTrend = (
  summariesByUserId: Record<string, AdminAccountPaymentSummaryResponse>,
  field: AdminAccountPaymentActivityDateField,
  currentStart: Date,
  currentEnd: Date,
  previousStart: Date,
): AdminAccountSignupTrend => {
  const currentCount = countAdminAccountPaymentActivityBetween(
    summariesByUserId,
    field,
    currentStart,
    currentEnd,
  );
  const previousCount = countAdminAccountPaymentActivityBetween(
    summariesByUserId,
    field,
    previousStart,
    currentStart,
  );
  const deltaCount = currentCount - previousCount;
  const deltaPercent = previousCount === 0
    ? (currentCount === 0 ? 0 : null)
    : Math.round((deltaCount / previousCount) * 1000) / 10;

  return { currentCount, previousCount, deltaCount, deltaPercent };
};

export const summarizeAdminAccountMonthlyPaymentInsights = (
  summariesByUserId: Record<string, AdminAccountPaymentSummaryResponse>,
  now: Date = new Date(),
): AdminAccountPaymentInsightTrendSummary => {
  const monthStart = startOfAdminAccountLocalMonth(now);
  const nextMonthStart = addAdminAccountMonths(monthStart, 1);
  const previousMonthStart = addAdminAccountMonths(monthStart, -1);
  const trend = (field: AdminAccountPaymentActivityDateField) =>
    buildAdminAccountPaymentActivityTrend(
      summariesByUserId,
      field,
      monthStart,
      nextMonthStart,
      previousMonthStart,
    );

  return {
    confirmedPaidSubscribers: trend('paidSubscriberActivityDates'),
    googlePlayPurchases: trend('storeSubscriptionActivityDates'),
    offerTrials: trend('offerTrialActivityDates'),
    firstLoginBenefits: trend('firstLoginBenefitActivityDates'),
    couponUsers: trend('couponUsageDates'),
    referralRewards: trend('referralRewardActivityDates'),
    manualBenefits: trend('manualBenefitActivityDates'),
  };
};

export const countCurrentAdminAccountPaidSubscribers = (
  summariesByUserId: Record<string, AdminAccountPaymentSummaryResponse>,
): number => Object.values(summariesByUserId).filter((summary) =>
  summary.appGroups.some((group) =>
    group.activeSubscriptionCount > 0 && group.currentAccessSource === 'PAID_SUBSCRIPTION'),
).length;

export const resolveLatestAdminAccountCreatedDate = (accounts: AdminAccountUser[]): string | null => {
  const latestTime = accounts.reduce<number | null>((currentLatest, account) => {
    const createdTime = getAdminAccountDateValue(account.createdDate);
    if (createdTime === null) {
      return currentLatest;
    }
    return currentLatest === null ? createdTime : Math.max(currentLatest, createdTime);
  }, null);

  return latestTime === null ? null : new Date(latestTime).toISOString();
};

export const countAdminAccountCreatedAfter = (
  accounts: AdminAccountUser[],
  lastCheckedCreatedDate: string | null | undefined,
): number => {
  const lastCheckedTime = getAdminAccountDateValue(lastCheckedCreatedDate);
  return accounts.filter((account) => {
    const createdTime = getAdminAccountDateValue(account.createdDate);
    return createdTime !== null && (lastCheckedTime === null || createdTime > lastCheckedTime);
  }).length;
};

export const normalizeAdminAccountUsers = (payload: unknown): AdminAccountUser[] => {
  const unwrappedPayload = unwrapJacksonEnvelope(payload);

  if (Array.isArray(unwrappedPayload)) {
    return unwrappedPayload
      .map(normalizeAdminAccountUser)
      .filter((account): account is AdminAccountUser => account !== null);
  }

  if (!unwrappedPayload || typeof unwrappedPayload !== 'object') {
    return [];
  }

  const container = unwrappedPayload as Record<string, unknown>;
  const candidates = [
    container['users'],
    container['items'],
    container['data'],
    container['content'],
    container['results'],
    container['accounts'],
  ];

  for (const candidate of candidates) {
    const unwrappedCandidate = unwrapJacksonEnvelope(candidate);
    if (Array.isArray(unwrappedCandidate)) {
      return unwrappedCandidate
        .map(normalizeAdminAccountUser)
        .filter((account): account is AdminAccountUser => account !== null);
    }
  }

  return [];
};

export const normalizeAdminAccountPaymentSummaries = (payload: unknown): AdminAccountPaymentSummaryResponse[] => {
  const unwrappedPayload = unwrapJacksonEnvelope(payload);

  if (Array.isArray(unwrappedPayload)) {
    return unwrappedPayload
      .map(normalizeAdminAccountPaymentSummary)
      .filter((summary): summary is AdminAccountPaymentSummaryResponse => summary !== null);
  }

  if (!unwrappedPayload || typeof unwrappedPayload !== 'object') {
    return [];
  }

  const container = unwrappedPayload as Record<string, unknown>;
  const candidates = [
    container['summaries'],
    container['items'],
    container['data'],
    container['content'],
    container['results'],
  ];

  for (const candidate of candidates) {
    const unwrappedCandidate = unwrapJacksonEnvelope(candidate);
    if (Array.isArray(unwrappedCandidate)) {
      return unwrappedCandidate
        .map(normalizeAdminAccountPaymentSummary)
        .filter((summary): summary is AdminAccountPaymentSummaryResponse => summary !== null);
    }
  }

  return [];
};

export const normalizeAdminAccountPaymentDebug = (payload: unknown): AdminAccountPaymentDebugResponse | null => {
  const unwrappedPayload = unwrapJacksonEnvelope(payload);
  if (!unwrappedPayload || typeof unwrappedPayload !== 'object') {
    return null;
  }

  const record = unwrappedPayload as Record<string, unknown>;
  const userId = asString(record['userId']);
  if (!userId) {
    return null;
  }

  const activeSubscription = normalizeAdminAccountActiveSubscription(record['activeSubscription']);
  const rawOrders = unwrapJacksonEnvelope(record['recentOrders']);
  const rawEvents = unwrapJacksonEnvelope(record['recentSubscriptionEvents']);
  const recentOrders = Array.isArray(rawOrders)
    ? rawOrders
        .map(normalizeAdminAccountRecentOrder)
        .filter((order): order is AdminAccountRecentOrder => order !== null)
    : [];
  const recentSubscriptionEvents = Array.isArray(rawEvents)
    ? rawEvents
        .map(normalizeAdminAccountSubscriptionEvent)
        .filter((event): event is AdminAccountSubscriptionEvent => event !== null)
    : [];

  return {
    userId,
    currentPlanCode: asString(record['currentPlanCode']) || '',
    activeSubscription,
    recentOrders,
    recentSubscriptionEvents,
  };
};

export const normalizeAdminAccountReferralDebug = (payload: unknown): AdminAccountReferralDebugResponse | null => {
  if (!payload || typeof payload !== 'object') {
    return null;
  }

  const record = payload as Record<string, unknown>;
  const userId = asString(record['userId']) || asString(record['user_id']);
  if (!userId) {
    return null;
  }

  const rawUsageAsReferee = unwrapJacksonEnvelope(record['usageAsReferee'] ?? record['usage_as_referee']);
  const rawUsageAsReferrer = unwrapJacksonEnvelope(record['usageAsReferrer'] ?? record['usage_as_referrer']);
  const rawTransactions = unwrapJacksonEnvelope(
    record['recentPointTransactions'] ?? record['recent_point_transactions'],
  );

  return {
    userId,
    referralCode: normalizeAdminAccountReferralCode(record['referralCode'] ?? record['referral_code']),
    pointBalance: normalizeNumber(record['pointBalance'] ?? record['point_balance']),
    currentConfig: normalizeAdminAccountReferralConfig(record['currentConfig'] ?? record['current_config']),
    stats: normalizeAdminAccountReferralStats(record['stats']),
    usageAsReferee: Array.isArray(rawUsageAsReferee)
      ? rawUsageAsReferee
          .map(normalizeAdminAccountReferralUsage)
          .filter((usage): usage is AdminAccountReferralUsage => usage !== null)
      : [],
    usageAsReferrer: Array.isArray(rawUsageAsReferrer)
      ? rawUsageAsReferrer
          .map(normalizeAdminAccountReferralUsage)
          .filter((usage): usage is AdminAccountReferralUsage => usage !== null)
      : [],
    recentPointTransactions: Array.isArray(rawTransactions)
      ? rawTransactions
          .map(normalizeAdminAccountReferralPointTransaction)
          .filter((transaction): transaction is AdminAccountReferralPointTransaction => transaction !== null)
      : [],
  };
};

export const formatAdminAccountDateTime = (value: string | null | undefined): string => {
  if (!value) return '기록 없음';
  const parsed = parseDateLike(value);
  if (!parsed) return value;

  const parts = [
    parsed.getFullYear(),
    String(parsed.getMonth() + 1).padStart(2, '0'),
    String(parsed.getDate()).padStart(2, '0'),
  ];
  const time = `${String(parsed.getHours()).padStart(2, '0')}:${String(parsed.getMinutes()).padStart(2, '0')}`;
  return `${parts[0]}-${parts[1]}-${parts[2]} ${time}`;
};

export const formatAdminAccountMoney = (
  value: number | null | undefined,
  currency: string | null | undefined,
): string => {
  if (value === null || value === undefined) {
    return '-';
  }

  const normalizedCurrency = normalizeCurrencyCode(currency) || 'KRW';
  const fractionDigits = ZERO_DECIMAL_CURRENCIES.has(normalizedCurrency) ? 0 : 2;
  const normalizedAmount = fractionDigits === 0 ? value : value / 100;
  const locale = normalizedCurrency === 'KRW' ? 'ko-KR' : 'en-US';

  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: normalizedCurrency,
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    }).format(normalizedAmount);
  } catch {
    return `${normalizedCurrency} ${normalizedAmount.toFixed(fractionDigits)}`;
  }
};

const PAYMENT_PLATFORM_LABELS: Record<string, string> = {
  GOOGLE_PLAY: 'Google Play Billing',
  NICEPAY: 'NicePay',
  PAYPLE: 'Payple',
  TOSS_PAYMENTS: 'Toss Payments',
  PADDLE: 'Paddle',
};

export const formatAdminAccountPaymentPlatform = (value: string | null | undefined): string => {
  const normalized = normalizePaymentPlatform(value);
  if (!normalized) return '플랫폼 없음';
  return PAYMENT_PLATFORM_LABELS[normalized] ?? normalized;
};

export const formatAdminAccountStatus = (enabled: boolean): string => {
  return enabled ? 'Enabled' : 'Disabled';
};

export const formatAdminAccountSubscriptionDateLabel = (status: string | null | undefined): string => {
  if (!status) {
    return '기준일';
  }

  switch (status.toUpperCase()) {
    case 'ACTIVE':
    case 'ON_HOLD':
    case 'IN_GRACE_PERIOD':
      return '다음 결제';
    case 'CANCELED_BY_ADMIN':
    case 'CANCELED_BY_USER':
    case 'CANCELED':
    case 'CANCELLED':
      return '종료';
    case 'EXPIRED':
      return '만료';
    default:
      return '기준일';
  }
};

export const resolveAdminAccountSubscriptionDateValue = (
  status: string | null | undefined,
  nextBillingDate: string | null | undefined,
  endDate: string | null | undefined,
): string | null => {
  if (status) {
    switch (status.toUpperCase()) {
      case 'ACTIVE':
      case 'ON_HOLD':
      case 'IN_GRACE_PERIOD':
        return nextBillingDate || endDate || null;
      default:
        return endDate || nextBillingDate || null;
    }
  }

  return endDate || nextBillingDate || null;
};

export const hasAdminAccountSubscriptionStatusMismatch = (
  subscription: AdminAccountActiveSubscription | null | undefined,
): boolean => {
  if (!subscription?.status) {
    return false;
  }

  const effectiveDate = resolveAdminAccountSubscriptionDateValue(
    subscription.status,
    subscription.nextBillingDate,
    subscription.endDate,
  );
  const parsedEffectiveDate = parseDateLike(effectiveDate);
  if (!parsedEffectiveDate) {
    return false;
  }

  if (parsedEffectiveDate.getTime() < Date.now()) {
    return false;
  }

  return !ADMIN_ACCOUNT_CURRENT_ACCESS_STATUSES.has(subscription.status.toUpperCase());
};

export const isAdminAccountPendingOrder = (status: string | null | undefined): boolean => {
  if (!status) return false;
  return ADMIN_ACCOUNT_PENDING_ORDER_STATUSES.has(status.toUpperCase());
};

export const filterAdminAccountRecentOrders = (
  orders: AdminAccountRecentOrder[],
  includePending: boolean = false,
): AdminAccountRecentOrder[] => {
  if (includePending) {
    return orders;
  }

  return orders.filter((order) => !isAdminAccountPendingOrder(order.status));
};

export const countAdminAccountPendingOrders = (
  orders: AdminAccountRecentOrder[],
): number => orders.filter((order) => isAdminAccountPendingOrder(order.status)).length;

const resolveAdminAccountPlanFamily = (
  productPlanCode: string | null | undefined,
  productName: string | null | undefined,
): string | null => {
  const normalizedPlanCode = productPlanCode?.trim().toUpperCase();
  if (normalizedPlanCode) {
    if (normalizedPlanCode.startsWith('SAJU_')) {
      return 'SAJU';
    }
    return normalizedPlanCode.split('_').filter(Boolean)[0] || normalizedPlanCode;
  }

  const normalizedProductName = productName?.trim().toUpperCase();
  if (!normalizedProductName) {
    return null;
  }
  if (normalizedProductName.includes('SAJU') || normalizedProductName.includes('사주')) {
    return 'SAJU';
  }
  return normalizedProductName;
};

const resolveAdminAccountPlanIdentity = (
  productPlanCode: string | null | undefined,
  productName: string | null | undefined,
): string | null => {
  const normalizedPlanCode = productPlanCode?.trim().toUpperCase();
  if (normalizedPlanCode) {
    return normalizedPlanCode.replace(/_YEARLY$/, '');
  }

  return productName?.trim().toUpperCase() || null;
};

const isGooglePlayPaymentPlatform = (paymentPlatform: string | null | undefined): boolean => {
  return normalizePaymentPlatform(paymentPlatform) === 'GOOGLE_PLAY';
};

const isSameAdminAccountPlanFamily = (
  order: Partial<Pick<AdminAccountRecentOrder, 'productPlanCode' | 'productName'>>,
  subscription: Partial<Pick<AdminAccountActiveSubscription, 'productPlanCode' | 'productName'>>,
): boolean => {
  const orderFamily = resolveAdminAccountPlanFamily(order.productPlanCode, order.productName);
  const subscriptionFamily = resolveAdminAccountPlanFamily(subscription.productPlanCode, subscription.productName);
  return Boolean(orderFamily && subscriptionFamily && orderFamily === subscriptionFamily);
};

const isSameAdminAccountPlanIdentity = (
  order: Partial<Pick<AdminAccountRecentOrder, 'productPlanCode' | 'productName'>>,
  subscription: Partial<Pick<AdminAccountActiveSubscription, 'productPlanCode' | 'productName'>>,
): boolean => {
  const orderPlan = resolveAdminAccountPlanIdentity(order.productPlanCode, order.productName);
  const subscriptionPlan = resolveAdminAccountPlanIdentity(subscription.productPlanCode, subscription.productName);
  return Boolean(orderPlan && subscriptionPlan && orderPlan === subscriptionPlan);
};

export const canAdminRequestRefundForOrder = (
  order: (
    Pick<AdminAccountRecentOrder, 'orderId' | 'status'>
    & Partial<Pick<AdminAccountRecentOrder, 'amount' | 'paymentPlatform' | 'productPlanCode' | 'productName' | 'refundRequestId' | 'refundRequestStatus'>>
  ) | null | undefined,
  activeSubscription?: (
    Pick<AdminAccountActiveSubscription, 'orderId'>
    & Partial<Pick<AdminAccountActiveSubscription, 'paymentPlatform' | 'productPlanCode' | 'productName'>>
  ) | null,
): boolean => {
  if (!order?.orderId) {
    return false;
  }
  if (!order.status) {
    return false;
  }
  if (!ADMIN_ACCOUNT_REFUNDABLE_ORDER_STATUSES.has(order.status.toUpperCase())) {
    return false;
  }
  if (typeof order.amount === 'number' && Number.isFinite(order.amount) && order.amount <= 0) {
    return false;
  }
  const refundRequestStatus = order.refundRequestStatus?.trim().toUpperCase();
  const hasProcessableRefundRequest = Boolean(
    refundRequestStatus && ADMIN_ACCOUNT_PROCESSABLE_REFUND_REQUEST_STATUSES.has(refundRequestStatus),
  );
  if (refundRequestStatus && !hasProcessableRefundRequest) {
    return false;
  }
  if (hasProcessableRefundRequest) {
    return true;
  }
  if (!refundRequestStatus && order.refundRequestId !== null && order.refundRequestId !== undefined) {
    return false;
  }

  if (!activeSubscription) {
    return true;
  }

  if (
    isGooglePlayPaymentPlatform(order.paymentPlatform)
    && isGooglePlayPaymentPlatform(activeSubscription?.paymentPlatform)
    && isSameAdminAccountPlanFamily(order, activeSubscription ?? {})
  ) {
    const activeOrderId = activeSubscription.orderId?.trim();
    if (activeOrderId) {
      return order.orderId === activeOrderId;
    }
    return isSameAdminAccountPlanIdentity(order, activeSubscription);
  }

  return true;
};

export const getAdminRefundActionLabel = (
  order: Partial<Pick<AdminAccountRecentOrder, 'refundRequestStatus'>> | null | undefined,
): string => {
  const refundRequestStatus = order?.refundRequestStatus?.trim().toUpperCase();
  if (refundRequestStatus === 'FAILED') {
    return '환불 재시도';
  }
  return refundRequestStatus && ADMIN_ACCOUNT_PROCESSABLE_REFUND_REQUEST_STATUSES.has(refundRequestStatus)
    ? '환불 처리'
    : '환불 요청';
};

export const canAdminCancelSubscription = (
  subscription: Pick<AdminAccountActiveSubscription, 'id' | 'status'> | null | undefined,
): boolean => {
  if (!subscription?.id) {
    return false;
  }
  if (!subscription.status) {
    return false;
  }
  return ADMIN_ACCOUNT_CANCELABLE_SUBSCRIPTION_STATUSES.has(subscription.status.toUpperCase());
};

export const normalizeAdminAccountUserId = (account: AdminAccountUser | null | undefined): string => {
  if (!account) return '';
  return getAdminAccountUserKey(account);
};

export const normalizeAdminAccountUser = (payload: unknown): AdminAccountUser | null => {
  if (!payload || typeof payload !== 'object') {
    return null;
  }

  const record = payload as Record<string, unknown>;
  const id = asString(record['id']) || asString(record['userId']) || asString(record['uuid']);
  if (!id) {
    return null;
  }

  const roles = normalizeStringCollection(record['roles']);

  return {
    id,
    username: asString(record['username']) || id,
    email: asString(record['email']) || '',
    displayName: asString(record['displayName']) || '',
    registrationApplications: normalizeRegistrationApplications(record),
    enabled: typeof record['enabled'] === 'boolean' ? record['enabled'] : true,
    roles,
    createdDate: asString(record['createdDate']) || asString(record['createdAt']) || null,
    lastLoginDate: asString(record['lastLoginDate']) || asString(record['lastLoginAt']) || null,
  };
};

const normalizeAdminAccountPaymentSummary = (payload: unknown): AdminAccountPaymentSummaryResponse | null => {
  if (!payload || typeof payload !== 'object') {
    return null;
  }

  const record = payload as Record<string, unknown>;
  const userId = asString(record['userId']);
  if (!userId) {
    return null;
  }

  const rawGroups = unwrapJacksonEnvelope(record['appGroups']);
  const appGroups = Array.isArray(rawGroups)
    ? sortAppGroups(
        rawGroups
          .map(normalizeAdminAccountPaymentSummaryAppGroup)
          .filter((group): group is AdminAccountPaymentSummaryAppGroup => group !== null)
      )
    : [];

  return {
    userId,
    appGroups,
  };
};

const normalizeAdminAccountPaymentSummaryAppGroup = (payload: unknown): AdminAccountPaymentSummaryAppGroup | null => {
  if (!payload || typeof payload !== 'object') {
    return null;
  }

  const record = payload as Record<string, unknown>;
  const appKey = asString(record['appKey']);
  const appLabel = asString(record['appLabel']);
  if (!appKey || !appLabel) {
    return null;
  }

  const rawSummaries = unwrapJacksonEnvelope(record['summaries']);
  const summaries = Array.isArray(rawSummaries)
    ? rawSummaries
        .map(normalizeAdminAccountPaymentSummaryItem)
        .filter((summary): summary is AdminAccountPaymentSummaryItem => summary !== null)
    : [];

  return {
    appKey,
    appLabel,
    activeSubscriptionCount: normalizeNumber(record['activeSubscriptionCount']),
    currentAccessSource: normalizeAdminAccountCurrentAccessSource(
      record['currentAccessSource'] ?? record['current_access_source'],
    ),
    currentAccessReference: asString(record['currentAccessReference'])
      || asString(record['current_access_reference']),
    currentAccessStartedAt: asString(record['currentAccessStartedAt'])
      || asString(record['current_access_started_at']),
    offerTrialState: normalizeAdminAccountOfferTrialState(
      record['offerTrialState'] ?? record['offer_trial_state'],
    ),
    offerId: asString(record['offerId']) || asString(record['offer_id']),
    offerTrialEndsAt: asString(record['offerTrialEndsAt']) || asString(record['offer_trial_ends_at']),
    offerPlatform: normalizePaymentPlatform(record['offerPlatform'] ?? record['offer_platform']),
    couponUsageCount: normalizeNumber(record['couponUsageCount'] ?? record['coupon_usage_count']),
    latestCouponCode: asString(record['latestCouponCode']) || asString(record['latest_coupon_code']),
    latestCouponType: asString(record['latestCouponType']) || asString(record['latest_coupon_type']),
    latestCouponTrialDays: normalizeOptionalNumber(
      record['latestCouponTrialDays'] ?? record['latest_coupon_trial_days'],
    ),
    latestCouponUsedAt: asString(record['latestCouponUsedAt']) || asString(record['latest_coupon_used_at']),
    nextPaymentState: normalizeAdminAccountNextPaymentState(
      record['nextPaymentState'] ?? record['next_payment_state'],
    ),
    nextPaymentAt: asString(record['nextPaymentAt']) || asString(record['next_payment_at']),
    nextPaymentPlatform: normalizePaymentPlatform(
      record['nextPaymentPlatform'] ?? record['next_payment_platform'],
    ),
    paidSubscriberActivityDates: normalizeStringCollection(
      record['paidSubscriberActivityDates'] ?? record['paid_subscriber_activity_dates'],
    ),
    storeSubscriptionActivityDates: normalizeStringCollection(
      record['storeSubscriptionActivityDates'] ?? record['store_subscription_activity_dates'],
    ),
    offerTrialActivityDates: normalizeStringCollection(
      record['offerTrialActivityDates'] ?? record['offer_trial_activity_dates'],
    ),
    firstLoginBenefitActivityDates: normalizeStringCollection(
      record['firstLoginBenefitActivityDates'] ?? record['first_login_benefit_activity_dates'],
    ),
    couponUsageDates: normalizeStringCollection(
      record['couponUsageDates'] ?? record['coupon_usage_dates'],
    ),
    referralRewardActivityDates: normalizeStringCollection(
      record['referralRewardActivityDates'] ?? record['referral_reward_activity_dates'],
    ),
    manualBenefitActivityDates: normalizeStringCollection(
      record['manualBenefitActivityDates'] ?? record['manual_benefit_activity_dates'],
    ),
    summaries,
  };
};

const normalizeAdminAccountCurrentAccessSource = (value: unknown): AdminAccountCurrentAccessSource => {
  const source = asString(value)?.trim().toUpperCase();
  if (source === 'PAID_SUBSCRIPTION'
    || source === 'STORE_SUBSCRIPTION'
    || source === 'STORE_OFFER_TRIAL'
    || source === 'COUPON'
    || source === 'REFERRAL_COUPON'
    || source === 'REFERRAL_REWARD'
    || source === 'FIRST_LOGIN_BENEFIT'
    || source === 'MANUAL_BENEFIT'
    || source === 'OTHER_BENEFIT'
    || source === 'NONE') {
    return source;
  }
  return 'NONE';
};

const normalizeAdminAccountOfferTrialState = (value: unknown): AdminAccountOfferTrialState => {
  const state = asString(value)?.trim().toUpperCase();
  if (state === 'ACTIVE' || state === 'ENDED' || state === 'OFFER_APPLIED' || state === 'NONE') {
    return state;
  }
  return 'NONE';
};

const normalizeAdminAccountNextPaymentState = (value: unknown): AdminAccountNextPaymentState => {
  const state = asString(value)?.trim().toUpperCase();
  if (state === 'SCHEDULED'
    || state === 'STORE_AUTORENEWING'
    || state === 'STORE_NOT_RENEWING'
    || state === 'STORE_STATUS_UNKNOWN'
    || state === 'EXTERNAL_PROVIDER'
    || state === 'PAYMENT_FAILURE'
    || state === 'NONE') {
    return state;
  }
  return 'NONE';
};

const normalizeAdminAccountPaymentSummaryItem = (payload: unknown): AdminAccountPaymentSummaryItem | null => {
  if (!payload || typeof payload !== 'object') {
    return null;
  }

  const record = payload as Record<string, unknown>;
  return {
    productPlanCode: asString(record['productPlanCode']) || '',
    planLabel: asString(record['planLabel']) || '',
    status: asString(record['status']) || '',
    billingCycle: asString(record['billingCycle']),
    paymentPlatform: normalizePaymentPlatform(record['paymentPlatform'] ?? record['paymentMethod'] ?? record['platform']),
    expiresAt: asString(record['expiresAt']),
    primary: record['primary'] === true,
    failureCategory: asString(record['failureCategory']) || asString(record['failure_category']),
  };
};

const normalizeAdminAccountReferralCode = (payload: unknown): AdminAccountReferralCode | null => {
  if (!payload || typeof payload !== 'object') {
    return null;
  }

  const record = payload as Record<string, unknown>;
  const code = asString(record['code']);
  const userId = asString(record['userId']) || asString(record['user_id']);
  if (!code || !userId) {
    return null;
  }

  return {
    code,
    userId,
    createdAt: asString(record['createdAt']) || asString(record['created_at']),
  };
};

const normalizeAdminAccountReferralConfig = (payload: unknown): AdminAccountReferralConfig | null => {
  if (!payload || typeof payload !== 'object') {
    return null;
  }

  const record = payload as Record<string, unknown>;
  const tier = asString(record['tier']);
  if (!tier) {
    return null;
  }

  return {
    tier,
    maxInvites: normalizeNumber(record['maxInvites'] ?? record['max_invites']),
    referrerDays: normalizeNumber(record['referrerDays'] ?? record['referrer_days']),
    refereeDays: normalizeNumber(record['refereeDays'] ?? record['referee_days']),
    referrerPoints: normalizeNumber(record['referrerPoints'] ?? record['referrer_points']),
    refereePoints: normalizeNumber(record['refereePoints'] ?? record['referee_points']),
  };
};

const normalizeAdminAccountReferralStats = (payload: unknown): AdminAccountReferralStats | null => {
  if (!payload || typeof payload !== 'object') {
    return null;
  }

  const record = payload as Record<string, unknown>;
  const tier = asString(record['tier']);
  if (!tier) {
    return null;
  }

  return {
    inviteCount: normalizeNumber(record['inviteCount'] ?? record['invite_count']),
    remainingInvites: normalizeNumber(record['remainingInvites'] ?? record['remaining_invites']),
    maxInvites: normalizeNumber(record['maxInvites'] ?? record['max_invites']),
    tier,
  };
};

const normalizeAdminAccountReferralUsage = (payload: unknown): AdminAccountReferralUsage | null => {
  if (!payload || typeof payload !== 'object') {
    return null;
  }

  const record = payload as Record<string, unknown>;
  const id = asString(record['id']);
  const code = asString(record['code']);
  const referrerId = asString(record['referrerId']) || asString(record['referrer_id']);
  const refereeId = asString(record['refereeId']) || asString(record['referee_id']);
  if (!id || !code || !referrerId || !refereeId) {
    return null;
  }

  return {
    id,
    code,
    referrerId,
    refereeId,
    usedAt: asString(record['usedAt']) || asString(record['used_at']),
    status: asString(record['status']) || '',
    proGrantedAt: asString(record['proGrantedAt']) || asString(record['pro_granted_at']),
  };
};

const normalizeAdminAccountReferralPointTransaction = (
  payload: unknown,
): AdminAccountReferralPointTransaction | null => {
  if (!payload || typeof payload !== 'object') {
    return null;
  }

  const record = payload as Record<string, unknown>;
  const id = asString(record['id']);
  const userId = asString(record['userId']) || asString(record['user_id']);
  const transactionType = asString(record['transactionType']) || asString(record['transaction_type']);
  if (!id || !userId || !transactionType) {
    return null;
  }

  return {
    id,
    userId,
    amount: normalizeNumber(record['amount']),
    transactionType,
    description: asString(record['description']),
    createdAt: asString(record['createdAt']) || asString(record['created_at']),
    expiresAt: asString(record['expiresAt']) || asString(record['expires_at']),
  };
};

const normalizeAdminAccountActiveSubscription = (payload: unknown): AdminAccountActiveSubscription | null => {
  if (!payload || typeof payload !== 'object') {
    return null;
  }

  const record = payload as Record<string, unknown>;
  return {
    id: asString(record['id']) || '',
    orderId: asString(record['orderId']) || asString(record['order_id']),
    productName: asString(record['productName']) || '',
    productPlanCode: asString(record['productPlanCode']) || '',
    status: asString(record['status']) || '',
    startDate: asString(record['startDate']) || '',
    nextBillingDate: asString(record['nextBillingDate']),
    endDate: asString(record['endDate']) || '',
    paymentPlatform: normalizePaymentPlatform(record['paymentPlatform'] ?? record['paymentMethod'] ?? record['platform']),
    lastPaymentDate: asString(record['lastPaymentDate']) || asString(record['last_payment_date']),
    lastPaymentResult: asString(record['lastPaymentResult']) || asString(record['last_payment_result']),
    orderFailReason: asString(record['orderFailReason']) || asString(record['order_fail_reason']),
    failureCategory: asString(record['failureCategory']) || asString(record['failure_category']),
    refundPending: typeof record['refundPending'] === 'boolean' ? record['refundPending'] : null,
  };
};

const normalizeAdminAccountRecentOrder = (payload: unknown): AdminAccountRecentOrder | null => {
  if (!payload || typeof payload !== 'object') {
    return null;
  }

  const record = payload as Record<string, unknown>;
  const orderId = asString(record['orderId']);
  const rawId = record['id'];
  const id = typeof rawId === 'number' && Number.isFinite(rawId)
    ? rawId
    : asString(rawId) || orderId || '';
  if (id === '') {
    return null;
  }

  return {
    id,
    orderId,
    productName: asString(record['productName']) || '',
    productPlanCode: asString(record['productPlanCode']) || '',
    amount: normalizeNumber(record['amount']),
    currency: normalizeCurrencyCode(record['currency']),
    billingCycle: asString(record['billingCycle'])?.toUpperCase() || null,
    paymentPlatform: normalizePaymentPlatform(record['paymentPlatform'] ?? record['paymentMethod'] ?? record['platform']),
    status: asString(record['status']) || '',
    createdAt: asString(record['createdAt']) || '',
    refundRequestId: typeof record['refundRequestId'] === 'number' && Number.isFinite(record['refundRequestId'])
      ? record['refundRequestId'] as number
      : asString(record['refundRequestId']) || null,
    refundRequestStatus: asString(record['refundRequestStatus']) || null,
    refundRequestedAt: asString(record['refundRequestedAt']),
    failReason: asString(record['failReason']) || asString(record['fail_reason']),
    failureCategory: asString(record['failureCategory']) || asString(record['failure_category']),
  };
};

const normalizeAdminAccountSubscriptionEvent = (payload: unknown): AdminAccountSubscriptionEvent | null => {
  if (!payload || typeof payload !== 'object') {
    return null;
  }

  const record = payload as Record<string, unknown>;
  const rawDeltaDays = record['deltaDays'];
  const rawId = record['id'];
  const id = typeof rawId === 'number' && Number.isFinite(rawId)
    ? rawId
    : asString(rawId) || '';
  if (id === '') {
    return null;
  }

  return {
    id,
    eventType: asString(record['eventType']) || '',
    description: asString(record['description']),
    createdAt: asString(record['createdAt']),
    previousEndDate: asString(record['previousEndDate']),
    newEndDate: asString(record['newEndDate']),
    deltaDays:
      (typeof rawDeltaDays === 'number' && Number.isFinite(rawDeltaDays))
      || (typeof rawDeltaDays === 'string' && rawDeltaDays.trim() && Number.isFinite(Number(rawDeltaDays)))
        ? Number(rawDeltaDays)
        : null,
  };
};
